'use client';

import { FunctionComponent, ReactElement, useMemo } from 'react';

import { useBrandLocaleValue } from '@/react/hooks/useBrandLocaleValue';
import { IImage } from '@/services/models/Media/Image';
import type { IPlayerSettings, IVideo } from '@/services/models/Media/Video';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { ISliderProps, Slide, Slider } from '../../Slider';
import { ISliderPaginationThumbnail } from '../../Slider/components/Pagination/ISliderPaginationOptions';
import { Image } from '../../core-ui/Image';

import { Breakpoint, Breakpoints } from '../../core-ui/Breakpoints';
import { PDPVideo } from '../PDPVideo';

import S from './styles.base.module.scss';
import { SliderDirection } from '../../Slider/ISliderOptions';

export interface IMainCarouselProps {
  /** Images to display. */
  images: Array<IImage>;
  /** Videos to display. */
  videos: Array<IVideo>;
}

/**
 * Generates the new thumbnails based on the active images.
 * @param images - Images off the product model.
 * @returns An array of slider thumbnail objects.
 */
function generateThumbnails(
  images: ReadonlyArray<IImage>
): Array<ISliderPaginationThumbnail> {
  return images.map((image) => {
    return { src: image.src, alt: image.alt! };
  });
}

/** The aspect ratio of a 1:1 image. */
const ASPECT_RATIO_1_1 = 1;

/**
 * Generates slides based on an array of images.
 *
 * Adds priority to the first image on slider to add eager initialization on the main image, improving LCP.
 *
 * @param images - Images off the product model.
 * @returns An array of react elements.
 */
function generateSliderImages(
  images: ReadonlyArray<IImage>
): Array<ReactElement<unknown>> {
  return images.map((imageObject, index) => {
    const image = {
      src: imageObject.src,
      alt: imageObject.alt,
      uuid: imageObject.uuid,
      aspectRatio: ASPECT_RATIO_1_1
    };

    return (
      <Slide key={image.src}>
        <Image
          enhanceOnZoom
          image={image}
          fit="contain"
          useSkeletonLoader={false}
          className={S.imageContainer}
          priority={index === 0}
        />
      </Slide>
    );
  });
}

/**
 * Generates slides based on an array of videos.
 * @param videos - Videos off the product model.
 * @param settings - Video settings for the player.
 * @returns An array of react elements.
 */
function generateSliderVideos(
  videos: ReadonlyArray<IVideo>,
  settings: IPlayerSettings
): Array<ReactElement> {
  return videos.map((video) => {
    return (
      <Slide key={video.uuid}>
        <PDPVideo video={{ ...video, settings }} />
      </Slide>
    );
  });
}

/** The main carousel for the PDP. */
export const MainCarousel: FunctionComponent<IMainCarouselProps> = ({
  images,
  videos
}) => {
  const mainImages = useMemo(() => generateSliderImages(images), [images]);

  const productConfig = ConfigurationService.getConfig('product');

  const videoSettingsDesktopConfig = productConfig.getSetting(
    'details.videoSettingsDesktop'
  );

  const mainVideos = useMemo(() => {
    const settings: IPlayerSettings = {
      autoplay: videoSettingsDesktopConfig.autoplay.value,
      muted: videoSettingsDesktopConfig.muted.value,
      loop: videoSettingsDesktopConfig.loop.value
    };
    return generateSliderVideos(videos, settings);
  }, [videos, videoSettingsDesktopConfig]);

  const videoSettingsMobileConfig = productConfig.getSetting(
    'details.videoSettingsMobile'
  );

  const mainVideosMobile = useMemo(() => {
    const settings: IPlayerSettings = {
      autoplay: videoSettingsMobileConfig.autoplay.value,
      muted: videoSettingsMobileConfig.muted.value,
      loop: videoSettingsMobileConfig.loop.value
    };

    return generateSliderVideos(videos, settings);
  }, [videos, videoSettingsMobileConfig]);

  const desktopSliderConfig = useBrandLocaleValue<ISliderProps>(
    () => ({
      default: {
        height: '100%',
        navigationWrapperClassName: S.navigationWrapper,
        pagination: {
          enabled: true,
          position: 'left',
          thumbnails: generateThumbnails(images),
          wrapperClassName: S.thumbnails,
          inset: false,
          direction: SliderDirection.Vertical
        },
        loop: true,
        navigation: {
          inset: true,
          arrowVariant: 'arrow',
          buttonClassName: S.navigation
        }
      },
      AHNU: {
        height: '100%',
        navigationWrapperClassName: S.navigationWrapper,
        pagination: {
          enabled: true,
          position: 'bottom',
          thumbnails: generateThumbnails(images),
          wrapperClassName: S.thumbnails,
          inset: false,
          direction: SliderDirection.Horizontal
        },
        loop: true,
        navigation: {
          inset: true,
          arrowVariant: 'angleLight',
          buttonClassName: S.navigation
        }
      }
    }),
    [images]
  );

  const mobileSliderConfig = {
    height: 'auto',
    loop: true,
    navigation: false,
    pagination: {
      inset: false,
      activeClassName: S.activeCircle,
      wrapperClassName: S.pagination,
      buttonClassName: S.circle,
      direction: 'horizontal'
    }
  } as ISliderProps;

  return (
    <div className={S.container}>
      <Breakpoints>
        <Breakpoint media="desktop">
          <Slider {...desktopSliderConfig}>
            {mainImages}
            {mainVideos}
          </Slider>
        </Breakpoint>
        <Breakpoint default>
          <Slider {...mobileSliderConfig}>
            {mainImages}
            {mainVideosMobile}
          </Slider>
        </Breakpoint>
      </Breakpoints>
    </div>
  );
};
